import React, { useState, useEffect } from 'react';
import { postAPI } from '../app/api';
import './PopupDisplay.css';

/**
 * PopupDisplay Component - Displays popups on pages based on frequency and settings
 * This component should be placed in the main layout wrapper that renders on all pages
 */
const PopupDisplay = ({ currentPage = 'home' }) => {
  const [popups, setPopups] = useState([]);
  const [displayedPopups, setDisplayedPopups] = useState(new Set());
  const [frequencyTracker, setFrequencyTracker] = useState({});
  const [closedPopups, setClosedPopups] = useState(new Set());

  /**
   * Load frequency tracker from localStorage on mount
   */
  useEffect(() => {
    const storedTracker = localStorage.getItem('popupFrequencyTracker');
    if (storedTracker) {
      try {
        setFrequencyTracker(JSON.parse(storedTracker));
      } catch (err) {
        console.error('Error parsing popup tracker:', err);
      }
    }
  }, []);

  /**
   * Fetch popups for current page
   */
  useEffect(() => {
    fetchPopupsForPage();
  }, [currentPage]);

  /**
   * Fetch popups from API
   */
  const fetchPopupsForPage = async () => {
    try {
      const response = await postAPI(`popup-data/fetchData/${currentPage}`, {});

      if (response.status === 1 && response.data) {
        setPopups(response.data);
      }
    } catch (error) {
      console.error('Error fetching popups:', error);
    }
  };

  /**
   * Check if popup should be displayed based on frequency rules
   * Frequency values: 0 = every visit, 1 = once per session, 2 = once per day, 3 = once per week, 4 = once only
   */
  const shouldDisplayPopup = (popup) => {
    // Check if user has closed this popup in current session
    if (closedPopups.has(popup.id)) {
      return false;
    }

    const trackerKey = `popup_${popup.id}`;
    const trackedData = frequencyTracker[trackerKey];
    const now = new Date();
    const freq = parseInt(popup.frequency) || 0;

    switch (freq) {
      case 0: // every_visit
        return true;

      case 1: // once_per_session
        return !displayedPopups.has(popup.id);

      case 2: // once_per_day
        if (!trackedData) return true;
        const trackedDate = new Date(trackedData.lastShown);
        return !isSameDay(trackedDate, now);

      case 3: // once_per_week
        if (!trackedData) return true;
        const trackedWeek = new Date(trackedData.lastShown);
        return !isSameWeek(trackedWeek, now);

      case 4: // once_only
        return !trackedData || !trackedData.shown;

      default:
        return true;
    }
  };

  /**
   * Handle displaying popup - update tracking and UI state
   */
  const handlePopupDisplay = (popupId) => {
    // Update session tracker
    const updatedDisplayed = new Set(displayedPopups);
    updatedDisplayed.add(popupId);
    setDisplayedPopups(updatedDisplayed);

    // Update localStorage tracker
    const updatedTracker = { ...frequencyTracker };
    updatedTracker[`popup_${popupId}`] = {
      lastShown: new Date().toISOString(),
      shown: true,
      count: (updatedTracker[`popup_${popupId}`]?.count || 0) + 1
    };
    setFrequencyTracker(updatedTracker);
    localStorage.setItem('popupFrequencyTracker', JSON.stringify(updatedTracker));
  };

  /**
   * Handle closing popup
   */
  const handleClosePopup = (popupId) => {
    // Add to closed popups set for current session
    const updatedClosed = new Set(closedPopups);
    updatedClosed.add(popupId);
    setClosedPopups(updatedClosed);
    
    handlePopupDisplay(popupId);
  };

  /**
   * Check if two dates are same day
   */
  const isSameDay = (date1, date2) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  /**
   * Check if two dates are in same week
   */
  const isSameWeek = (date1, date2) => {
    const getWeek = (date) => {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    };

    return (
      getWeek(date1) === getWeek(date2) &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  /**
   * Get position CSS for popup
   */
  const getPositionCSS = (popupPosition) => {
    const positions = {
      'top-left': { top: '20px', left: '20px', right: 'auto', bottom: 'auto' },
      'top-center': { top: '20px', left: '50%', right: 'auto', bottom: 'auto', transform: 'translateX(-50%)' },
      'top-right': { top: '20px', right: '20px', left: 'auto', bottom: 'auto' },
      'center-left': { top: '50%', left: '20px', right: 'auto', bottom: 'auto', transform: 'translateY(-50%)' },
      'center': { top: '50%', left: '50%', right: 'auto', bottom: 'auto', transform: 'translate(-50%, -50%)' },
      'center-right': { top: '50%', right: '20px', left: 'auto', bottom: 'auto', transform: 'translateY(-50%)' },
      'bottom-left': { bottom: '20px', left: '20px', right: 'auto', top: 'auto' },
      'bottom-center': { bottom: '20px', left: '50%', right: 'auto', top: 'auto', transform: 'translateX(-50%)' },
      'bottom-right': { bottom: '20px', right: '20px', left: 'auto', top: 'auto' }
    };

    return positions[popupPosition] || positions['center'];
  };

  /**
   * Get image position style
   */
  const getImagePositionCSS = (imagePosition) => {
    const positions = {
      'top': 'column',
      'left': 'row',
      'right': 'row-reverse',
      'bottom': 'column-reverse'
    };

    return positions[imagePosition] || 'column';
  };

  /**
   * Get title style
   */
  const getTitleStyle = (popup) => ({
    color: popup.font_color || '#000000',
    fontFamily: popup.font_style || 'Arial',
    fontWeight: popup.font_weight || 'bold',
    fontSize: popup.font_size || '24px',
    margin: '0 0 10px 0'
  });

  /**
   * Get description style
   */
  const getDescriptionStyle = (popup) => ({
    color: popup.font_color || '#333333',
    fontFamily: popup.font_style || 'Arial',
    fontWeight: popup.font_weight || 'normal',
    fontSize: popup.font_size || '16px',
    margin: '0',
    lineHeight: '1.5'
  });

  /**
   * Get container style
   */
  const getContainerStyle = (popup) => {
    const position = getPositionCSS(popup.popup_position);
    let widthPercent = 80;
    if (popup.popup_size) {
      const parsed = parseInt(popup.popup_size);
      if (!isNaN(parsed)) widthPercent = parsed;
    }
    // Set height to 80% of viewport height
    let heightPercent = 80;
    return {
      ...position,
      position: 'fixed',
      backgroundColor: '#ffffff',
      backgroundImage: popup.bg_image ? `url(${popup.bg_image})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      padding: '24px',
      width: widthPercent + '%',
      height: heightPercent + 'vh',
      maxWidth: 'none',
      maxHeight: 'none',
      zIndex: 10000,
      fontFamily: 'Arial, sans-serif',
      overflow: 'auto'
    };
  };

  /**
   * Render popup content
   */
  const renderPopupContent = (popup) => {
    const imagePosition = getImagePositionCSS(popup.image_position);

    // Calculate image and content max heights for 80% popup
    const contentPadding = 48; // 24px top + 24px bottom
    const maxContentHeight = `calc(80vh - ${contentPadding}px)`;

    return (
      <div
        key={`popup-${popup.id}`}
        className="popup-container"
        style={getContainerStyle(popup)}
      >
        <button
          className="popup-close-btn"
          onClick={() => handleClosePopup(popup.id)}
          aria-label="Close popup"
        >
          ×
        </button>

        <div
          style={{
            display: 'flex',
            flexDirection: imagePosition,
            gap: '16px',
            alignItems: 'flex-start',
            maxHeight: maxContentHeight,
            overflow: 'auto',
            width: '100%'
          }}
        >
          {popup.image && (
            <img
              src={popup.image}
              alt={popup.title}
              style={{
                width: popup.image_width ? popup.image_width + '%' : undefined,
                height: popup.image_height && popup.image_height !== 'auto' ? popup.image_height + 'px' : undefined,
                maxWidth: '100%',
                maxHeight: '100%',
                borderRadius: '4px',
                objectFit: 'contain',
                flexShrink: 0,
                display: 'block',
                margin: imagePosition === 'column' || imagePosition === 'column-reverse' ? '0 auto 16px auto' : '0',
                background: '#fff'
              }}
            />
          )}

          <div style={{ flex: 1, maxHeight: maxContentHeight, overflow: 'auto' }}>
            {popup.title && (
              <h3 style={getTitleStyle(popup)}>
                {popup.title}
              </h3>
            )}

            {popup.description && (
              <p style={getDescriptionStyle(popup)}>
                {popup.description}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render only popups that should be displayed
  const popupsToDisplay = popups.filter(popup => shouldDisplayPopup(popup));

  // Track displayed popups - only run once when popups are first loaded
  // Use a ref to track if we've already processed these popups
  const processedPopupsRef = React.useRef(new Set());

  useEffect(() => {
    // Only process popups that haven't been processed yet
    popupsToDisplay.forEach(popup => {
      if (!processedPopupsRef.current.has(popup.id) && !displayedPopups.has(popup.id)) {
        processedPopupsRef.current.add(popup.id);
        
        // Update session tracker
        setDisplayedPopups(prev => {
          const updated = new Set(prev);
          updated.add(popup.id);
          return updated;
        });

        // Update localStorage tracker
        setFrequencyTracker(prev => {
          const updated = { ...prev };
          updated[`popup_${popup.id}`] = {
            lastShown: new Date().toISOString(),
            shown: true,
            count: (updated[`popup_${popup.id}`]?.count || 0) + 1
          };
          localStorage.setItem('popupFrequencyTracker', JSON.stringify(updated));
          return updated;
        });
      }
    });
  }, [popups]); // Only depend on popups array, not the filtered result

  if (popupsToDisplay.length === 0) {
    return null;
  }

  return (
    <div className="popup-display-container">
      {popupsToDisplay.map(popup => renderPopupContent(popup))}
    </div>
  );
};

export default PopupDisplay;
